import React, { useMemo } from "react";
import { useOnClickOutside } from "../../hooks/use-on-outside-click";

export default function FilterBoxWithDropdown({
  title,
  options = [],
  selectedOptions,
  setSelectedOptions,
  showDropdown,
  toggleDropdown,
  closeDropdown,
}: {
  title: string;
  options: { id: string | number; label: string; labelSubtext: string }[];
  selectedOptions: Array<string | number>;
  setSelectedOptions: (optionIds: Array<string | number>) => void;
  showDropdown: boolean;
  toggleDropdown: () => void;
  closeDropdown?: () => void;
}) {
  const divRef = React.useRef<HTMLDivElement>(null);

  useOnClickOutside(divRef, () => {
    closeDropdown?.();
  });

  return (
    <div
      ref={divRef}
      className="flex items-center justify-between px-8 py-4 bg-white border border-neutral-7 rounded-2 w-196 relative cursor-pointer "
      onClick={(e) => {
        toggleDropdown();
        e.stopPropagation();
      }}
    >
      <p className="text-11 text-neutral-3 truncate">{title}</p>
      <div className="w-16 h-16 flex items-center justify-between">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 320 512"
          className="w-16 h-16 fill-neutral-2"
        >
          <path d="M31.3 192h257.3c17.8 0 26.7 21.5 14.1 34.1L174.1 354.8c-7.8 7.8-20.5 7.8-28.3 0L17.2 226.1C4.6 213.5 13.5 192 31.3 192z" />
        </svg>
      </div>
      {showDropdown && (
        <FilterDropdown
          options={options}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          allowWrapLabels={true}
        />
      )}
    </div>
  );
}

export function FilterDropdown({
  options,
  selectedOptions,
  setSelectedOptions,
  allowWrapLabels = false,
}: {
  options: { id: string | number; label: string; labelSubtext: string }[];
  selectedOptions: Array<string | number>;
  setSelectedOptions: (optionIds: Array<string | number>) => void;
  allowWrapLabels?: boolean;
}) {
  const selectedOptionSet = useMemo(() => {
    return new Set(selectedOptions);
  }, [selectedOptions]);

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
      className="absolute max-w-288 bg-white top-28 z-10 px-16 py-8 shadow-tight-4 rounded-8 max-h-304 overflow-auto"
    >
      <div className="w-full bg-white space-y-8">
        {options.length > 0 ? (
          options.map(({ id, label, labelSubtext }) => (
            <div
              key={id}
              onClick={(e) => {
                const changeSet = new Set(selectedOptionSet);
                if (changeSet.has(id)) {
                  changeSet.delete(id);
                } else {
                  changeSet.add(id);
                }

                setSelectedOptions(Array.from(changeSet));
                e.stopPropagation();
              }}
              className="flex items-center space-x-8 rounded-4 cursor-pointer"
            >
              <div>
                <div className="flex flex-col items-center justify-center w-18 h-18">
                  <div className="relative flex flex-col items-center justify-center w-full h-full rounded-2">
                    <input
                      type="checkbox"
                      className="w-full h-full opacity-0"
                      name="checkbox"
                      checked={selectedOptionSet.has(id)}
                      onChange={() => {}}
                    />
                    <div className="absolute inset-0 checkbox-fill"></div>
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 28 28"
                      className="absolute hidden -top-3 -left-3 fill-white cursor-pointer fill-white"
                      preserveAspectRatio="xMidYMid meet"
                    >
                      <path d="M10.0547 16.8125C10.2891 17.0469 10.6875 17.0469 10.9219 16.8125L17.8125 9.92188C18.0469 9.6875 18.0469 9.28906 17.8125 9.05469L16.9688 8.21094C16.7344 7.97656 16.3594 7.97656 16.125 8.21094L10.5 13.8359L7.85156 11.2109C7.61719 10.9766 7.24219 10.9766 7.00781 11.2109L6.16406 12.0547C5.92969 12.2891 5.92969 12.6875 6.16406 12.9219L10.0547 16.8125Z"></path>
                    </svg>
                  </div>
                </div>
              </div>
              <div
                style={{ width: "calc(100% - 24px)" }}
                className="flex items-center justify-between text-13 font-700 text-neutral-4"
              >
                <div className="flex items-center space-x-4 w-80p">
                  <span
                    title={label}
                    className={`${
                      !allowWrapLabels ? "whitespace-nowrap" : ""
                    } max-w-full capitalize`}
                  >
                    {label}
                  </span>
                </div>
                {labelSubtext && (
                  <div className="pl-12 text-13 font-700 text-neutral-4">
                    {labelSubtext}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <span className="text-13 font-700 text-neutral-4 truncate">
            Insufficient data
          </span>
        )}
      </div>
    </div>
  );
}
